import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Alert, ArrowLeft2, QuestionFr } from "@bale/icons";
import {
  GeneralErrors,
  GeneralValidators,
  useInputHandler,
} from "hooks/useInputHandler";
import {
  NewButtonVariant,
  NewTextField,
  RadioButton,
  RadioButtonGroup,
  Switch,
  Title,
} from "@bale/ui";
import Checkbox from "components/commons/checkbox/checkbox";
import {
  calcViewCountForReservation,
  formatStrings,
  isUserException,
} from "utils/utils";
import { digitsToHindi, fromMoneyFormats, toMoneyFormat } from "@bale/utils";
import { strings } from "intl/fa";
import classNames from "classnames";
import commonStyles from "../Common.module.scss";
import styles from "./PublishStep.module.scss";
import CardWrapper from "components/commons/cardWrapper/CardWrapper";
import { useAppDispatch, useAppSelector } from "utils/store";
import {
  AdType,
  OrderAdActions,
  OrderAdPages,
} from "core/orderAd/orderAd.slice";
import { toast } from "react-toastify";
import { useRequest } from "../../../../../hooks/useRequest";
import { submitAdOrderThunk } from "../../../../../core/orderAd/orderAd.thunk";
import CapacityDatePicker from "components/modals/capacityDatePicker/CapacityDatePicker";
import { useMyPanel } from "hooks/useMyPanel";
import { useConfigs } from "hooks/useConfigs";
import { AdSpot } from "core/orderAd/orderAd.entity";
import { useCalculatedPrice } from "hooks/useCalculatedPrice";
import { useCampaignPrice } from "hooks/useCampaignPrice";
import Modal from "components/commons/modal/Modal";
import PriceFooter from "components/content/priceFooter/PriceFooter";
import { GeneralConfig } from "utils/constants";

type TermsProps = {
  checked: boolean;
  onChange: () => void;
};
export const TermsAndConditions: React.FC<
  React.PropsWithChildren<TermsProps>
> = (props) => {
  return (
    <div
      className={classNames(
        commonStyles.FormItem,
        styles.TermsAndConditionsWrapper
      )}
    >
      <div className={styles.CheckboxWrapper}>
        <Checkbox
          testId="terms-checkbox"
          checked={props.checked}
          onSelect={props.onChange}
        />
      </div>
      <p className={styles.TermsAndConditionsText}>
        {strings.dialog_ad_form.publish_step.all + " "}
        <a
          href="https://bale.ai/terms/#advertisement"
          target="_blank"
          rel="noopener noreferrer"
          className={styles.TermsAndConditionsLink}
        >
          {strings.dialog_ad_form.publish_step.terms_and_conditions_link_text}
        </a>
        {" " + strings.dialog_ad_form.publish_step.i_have_studied_and_agreed}
      </p>
    </div>
  );
};

type Props = {
  isCampaign?: boolean;
};
const PublishStep: React.FC<React.PropsWithChildren<Props>> = (props) => {
  const dispatch = useAppDispatch();
  const myPanel = useMyPanel();

  const {
    min_dialog_view,
    min_dialog_click,
    max_dialog_click,
    min_join_clicks,
    max_join_clicks,
    click_to_view_coef,
  } = useConfigs();

  const { loading } = useRequest(submitAdOrderThunk);
  const { view_count, click_count, desired_start_time, link, stop_value } =
    useAppSelector((state) => state.orderAd.info);
  const linkIsForceJoin = useAppSelector(
    (state) => state.orderAd.linkIsForceJoin
  );

  const [selectedOption, setSelectedOption] = useState<AdType>(
    linkIsForceJoin ? AdType.click : click_count ? AdType.click : AdType.view
  );

  const [isDateModalOpen, setIsDateModalOpen] = useState<boolean>(false);
  const { credit } = useMyPanel();

  const [stopValueMinError, setStopValueMinError] = useState<string>();
  const [stopValueMaxError, setStopValueMaxError] = useState<string>();

  const [typeHelpModal, setTypeHelpModal] = useState<boolean>(false);

  const stopValueViewCountHandler = useInputHandler({
    initialValue: stop_value ? String(stop_value) : "",
    beforeChange: fromMoneyFormats,
    numberOnly: true,
    rules: [GeneralValidators.required, GeneralValidators.divisibleBy(1000)],
    errorMessages: {
      [GeneralErrors.divisibleBy]: formatStrings(
        strings.dialog_ad_form.publish_step.divisible_error_view,
        { value: digitsToHindi("1000") }
      ),
      [GeneralErrors.MinimumAmount]: formatStrings(
        strings.dialog_ad_form.publish_step.min_view_count_error,
        {
          count: digitsToHindi(toMoneyFormat(min_dialog_view.toString())),
        }
      ),
    },
    formatter: toMoneyFormat,
  });

  const stopValueClickCountHandler = useInputHandler({
    initialValue: stop_value ? String(stop_value) : "",
    beforeChange: fromMoneyFormats,
    numberOnly: true,
    rules: [GeneralValidators.divisibleBy(100)],
    errorMessages: {
      [GeneralErrors.divisibleBy]: formatStrings(
        strings.dialog_ad_form.publish_step.divisible_error_click,
        { value: digitsToHindi("100") }
      ),
      [GeneralErrors.MinimumAmount]: formatStrings(
        strings.dialog_ad_form.publish_step.min_view_count_error,
        {
          count: digitsToHindi(toMoneyFormat(min_dialog_view.toString())),
        }
      ),
    },
    formatter: toMoneyFormat,
  });

  const viewCountHandler = useInputHandler({
    initialValue: view_count?.toString(),
    beforeChange: fromMoneyFormats,
    numberOnly: true,
    rules: [
      GeneralValidators.required,
      GeneralValidators.minAmount(min_dialog_view),
    ],
    errorMessages: {
      [GeneralErrors.Required]: strings.common.error.required,
      [GeneralErrors.MinimumAmount]: formatStrings(
        strings.dialog_ad_form.publish_step.min_view_count_error,
        {
          count: digitsToHindi(toMoneyFormat(min_dialog_view.toString())),
        }
      ),
    },
    formatter: toMoneyFormat,
  });

  const clickCountHandler = useInputHandler({
    initialValue: click_count?.toString(),
    beforeChange: fromMoneyFormats,
    numberOnly: true,
    rules: [
      GeneralValidators.required,
      GeneralValidators.minAmount(
        linkIsForceJoin ? min_join_clicks : min_dialog_click
      ),
      GeneralValidators.maxAmount(
        linkIsForceJoin ? max_join_clicks : max_dialog_click
      ),
    ],
    errorMessages: {
      [GeneralErrors.Required]: strings.common.error.required,
      [GeneralErrors.MinimumAmount]: formatStrings(
        linkIsForceJoin
          ? strings.dialog_ad_form.publish_step.min_join_click_count_error
          : strings.dialog_ad_form.publish_step.min_click_count_error,
        {
          count: digitsToHindi(
            toMoneyFormat(
              linkIsForceJoin
                ? min_join_clicks.toString()
                : min_dialog_click.toString()
            )
          ),
        }
      ),
      [GeneralErrors.MaximumAmount]: formatStrings(
        linkIsForceJoin
          ? strings.dialog_ad_form.publish_step.max_join_click_count_error
          : strings.dialog_ad_form.publish_step.max_click_count_error,
        {
          count: digitsToHindi(
            toMoneyFormat(
              linkIsForceJoin
                ? max_join_clicks.toString()
                : max_dialog_click.toString()
            )
          ),
        }
      ),
    },
    formatter: toMoneyFormat,
  });
  const isView = selectedOption === AdType.view;
  const campaignBasePrice = useCampaignPrice({
    description: link || "",
    adSpot: AdSpot.AdSpotDialog,
    adType: isView ? AdType.view : AdType.click,
  });
  const maxStopValue = Math.floor(credit / campaignBasePrice);

  const dateHandler = useInputHandler({
    initialValue: desired_start_time?.toString(),
  });

  const isInstant = useAppSelector((state) => state.orderAd.info.is_instant);
  const [termsAccepted, setTermsAccepted] = useState(false);
  const handleChangeTerms = useCallback(() => {
    setTermsAccepted((prevState) => !prevState);
  }, []);

  const handleOptionChange = (newOption: AdType) => {
    if (newOption === AdType.view && linkIsForceJoin) {
      toast.info(strings.dialog_ad_form.publish_step.errors.forceJoinProvided);
      return;
    }
    setSelectedOption(newOption);
    if (newOption === AdType.click) {
      viewCountHandler.setValue("");
      clickCountHandler.setValue(viewCountHandler.rawValue);
    } else {
      viewCountHandler.setValue(clickCountHandler.rawValue);
      clickCountHandler.setValue("");
    }
  };

  const handleInstantAdSwitchChange = (value: boolean) => {
    dispatch(OrderAdActions.setOrderAdInfo({ is_instant: !isInstant }));
    if (value)
      toast.info(
        formatStrings(strings.dialog_ad_form.publish_step.instant_ad_toast, {
          percentage: digitsToHindi(String(GeneralConfig.InstantPrice)),
        })
      );
  };

  const validViewOrClick =
    // campaign ads doesn't have click or view count
    props.isCampaign ||
    (selectedOption === AdType.click &&
      clickCountHandler.rawValue &&
      !clickCountHandler.error) ||
    (selectedOption === AdType.view &&
      viewCountHandler.rawValue &&
      !viewCountHandler.error);

  const isFormInvalid = useMemo(() => {
    if (isUserException(myPanel.owner_id)) {
      return false;
    }
    if (!validViewOrClick) {
      return true;
    }
    if (!termsAccepted) {
      return true;
    }

    if (selectedOption === AdType.view && stopValueViewCountHandler.error) {
      return true;
    }
    if (selectedOption === AdType.click && stopValueClickCountHandler.error) {
      return true;
    }
    if (stopValueMinError || stopValueMaxError) {
      return true;
    }
    return false;
  }, [myPanel.owner_id, termsAccepted, validViewOrClick]);

  const { loading: priceLoading, finalBasePrice } = useCalculatedPrice({
    viewCount: Number(viewCountHandler.rawValue) ?? 0,
    link: link ?? "",
    spot: AdSpot.AdSpotDialog,
    clickCount: Number(clickCountHandler.rawValue) ?? 0,
    is_instant: isInstant,
  });

  const saveData = () => {
    if (isFormInvalid) {
      toast.info(strings.common.error.all_required, {
        toastId: "all_required",
      });
      return false;
    }
    dispatch(
      OrderAdActions.setOrderAdInfo({
        is_instant: isInstant,
        click_count: parseInt(clickCountHandler.rawValue),
        view_count: parseInt(viewCountHandler.rawValue),
        desired_start_time: dateHandler.rawValue,
        adType: selectedOption,
        stop_value: Number(
          selectedOption === AdType.view
            ? stopValueViewCountHandler.rawValue
            : stopValueClickCountHandler.rawValue
        ),
      })
    );
    return true;
  };
  const handleSubmit = () => {
    let nextPage = OrderAdPages.DialogMoreSettings;
    if (props.isCampaign) {
      nextPage = OrderAdPages.CampaignMoreSettings;
    }
    saveData() && dispatch(OrderAdActions.setPage(nextPage));
  };

  useEffect(() => {
    if (viewCountHandler.rawValue !== "") {
      viewCountHandler.validate();
    }
    if (clickCountHandler.rawValue !== "") {
      clickCountHandler.validate();
    }
  }, [viewCountHandler.rawValue, clickCountHandler.rawValue]);

  useEffect(() => {
    setStopValueMaxError(undefined);
    setStopValueMinError(undefined);
    if (isView) {
      if (Number(stopValueViewCountHandler.rawValue) > maxStopValue) {
        setStopValueMaxError(
          formatStrings(strings.dialog_ad_form.publish_step.max_stop_error, {
            type: isView ? strings.common.view : strings.common.click,
            count: digitsToHindi(toMoneyFormat(maxStopValue.toString())),
          })
        );
      } else if (Number(stopValueViewCountHandler.rawValue) < 0) {
        setStopValueMinError(
          strings.dialog_ad_form.publish_step.min_stop_value_error
        );
      }
    } else {
      if (Number(stopValueClickCountHandler.rawValue) > maxStopValue) {
        setStopValueMaxError(
          formatStrings(strings.dialog_ad_form.publish_step.max_stop_error, {
            type: isView ? strings.common.view : strings.common.click,
            count: digitsToHindi(toMoneyFormat(maxStopValue.toString())),
          })
        );
      } else if (Number(stopValueClickCountHandler.rawValue) < 0) {
        setStopValueMinError(
          strings.dialog_ad_form.publish_step.min_stop_value_error
        );
      }
    }
  }, [
    maxStopValue,
    stopValueClickCountHandler.rawValue,
    stopValueViewCountHandler.rawValue,
  ]);

  return (
    <>
      <CardWrapper className={commonStyles.Card}>
        <div className={commonStyles.FormItem}>
          <RadioButtonGroup
            title={
              <Title
                title={strings.dialog_ad_form.publish_step.ad_type_title}
                className={commonStyles.titleWrapper}
                titleClassName={commonStyles.Title}
                suffixIcon={QuestionFr}
                suffixHandler={() => setTypeHelpModal(true)}
              />
            }
            divider={false}
          >
            <RadioButton
              key={"click"}
              checked={selectedOption === AdType.click}
              onChange={() => handleOptionChange(AdType.click)}
              text={strings.dialog_ad_form.publish_step.click_count}
              className={commonStyles.radioButton}
            />
            <RadioButton
              key={"view"}
              checked={selectedOption === AdType.view}
              onChange={() => handleOptionChange(AdType.view)}
              text={strings.dialog_ad_form.publish_step.view_count}
              className={commonStyles.radioButton}
              disabled={linkIsForceJoin}
            />
          </RadioButtonGroup>
        </div>
        {!props.isCampaign && (
          <div className={commonStyles.FormItem}>
            {selectedOption === AdType.view ? (
              <NewTextField
                {...viewCountHandler}
                className={commonStyles.TextField}
                direction={viewCountHandler.value ? "ltr" : "rtl"}
                data-testid="view-count"
                label={strings.dialog_ad_form.publish_step.view_count}
                placeholder={
                  strings.dialog_ad_form.publish_step.view_count_placeholder
                }
                helpText={
                  viewCountHandler.error
                    ? undefined
                    : viewCountHandler.rawValue
                      ? formatStrings(
                          strings.dialog_ad_form.publish_step.view_count_info,
                          {
                            count: digitsToHindi(
                              toMoneyFormat(min_dialog_view.toString())
                            ),
                            price: digitsToHindi(
                              toMoneyFormat(finalBasePrice.toString())
                            ),
                          }
                        )
                      : formatStrings(
                          strings.dialog_ad_form.publish_step
                            .min_view_count_error,
                          {
                            count: digitsToHindi(
                              toMoneyFormat(min_dialog_view.toString())
                            ),
                          }
                        )
                }
              />
            ) : (
              <NewTextField
                {...clickCountHandler}
                className={commonStyles.TextField}
                direction={clickCountHandler.value ? "ltr" : "rtl"}
                data-testid="click-count"
                label={strings.dialog_ad_form.publish_step.click_count}
                placeholder={
                  strings.dialog_ad_form.publish_step.click_count_placeholder
                }
                helpText={
                  clickCountHandler.error
                    ? undefined
                    : clickCountHandler.rawValue
                      ? formatStrings(
                          strings.dialog_ad_form.publish_step.click_count_info,
                          {
                            count: digitsToHindi(
                              toMoneyFormat(min_dialog_click.toString())
                            ),
                            price: digitsToHindi(
                              toMoneyFormat(finalBasePrice.toString())
                            ),
                          }
                        )
                      : formatStrings(
                          linkIsForceJoin
                            ? strings.dialog_ad_form.publish_step
                                .min_join_click_count_error
                            : strings.dialog_ad_form.publish_step
                                .min_click_count_error,
                          {
                            count: digitsToHindi(
                              toMoneyFormat(
                                linkIsForceJoin
                                  ? min_join_clicks.toString()
                                  : min_dialog_click.toString()
                              )
                            ),
                          }
                        )
                }
              />
            )}
          </div>
        )}
        {props.isCampaign && (
          <div className={styles.TagsWrapper}>
            <p className={styles.TagsTitle}>{strings.stop_campaign.title}</p>
            <p
              className={classNames(styles.TagsDescription)}
              style={{ marginBottom: 12 }}
            >
              {formatStrings(strings.stop_campaign.helper_campaign, {
                type:
                  selectedOption === AdType.view
                    ? strings.viewy
                    : strings.clicky,
              })}
            </p>
            {selectedOption === AdType.view ? (
              <NewTextField
                {...stopValueViewCountHandler}
                className={commonStyles.TextField}
                direction={stopValueViewCountHandler.value ? "ltr" : "rtl"}
                errorText={
                  stopValueViewCountHandler.error ||
                  stopValueMinError ||
                  stopValueMaxError
                }
                data-testid="view-count"
                label={
                  strings.dialog_ad_form.publish_step.view_count_stop_value
                }
                placeholder={
                  strings.dialog_ad_form.publish_step.view_count_placeholder
                }
              />
            ) : (
              <NewTextField
                {...stopValueClickCountHandler}
                className={commonStyles.TextField}
                errorText={
                  stopValueClickCountHandler.error ||
                  stopValueMinError ||
                  stopValueMaxError
                }
                direction={stopValueClickCountHandler.value ? "ltr" : "rtl"}
                data-testid="click-count"
                label={
                  strings.dialog_ad_form.publish_step.click_count_stop_value
                }
                placeholder={
                  strings.dialog_ad_form.publish_step.click_count_placeholder
                }
              />
            )}
          </div>
        )}
        <div className={styles.TagsWrapper}>
          <p className={styles.TagsTitle}>
            {strings.dialog_ad_form.publish_step.more_settings}
          </p>
        </div>
        {/* <TagsDropdown
          categories={categories}
          subcategories={subcategories}
          onCategoryChange={onCategoryChange}
          onSubcategoryChange={onSubcategoryChange}
        /> */}
        <SettingsOption
          title={strings.dialog_ad_form.publish_step.show_calender}
          helpText={strings.dialog_ad_form.publish_step.calender_description}
          onClick={() => setIsDateModalOpen(true)}
        >
          <ArrowLeft2 size={24} />
        </SettingsOption>
        {!props.isCampaign && (
          <SettingsOption
            title={strings.dialog_ad_form.publish_step.instant_ad_display}
            helpText={formatStrings(
              strings.dialog_ad_form.publish_step.instant_ad_help,
              {
                ["percentage"]: digitsToHindi(
                  String(GeneralConfig.InstantPrice)
                ),
              }
            )}
          >
            <Switch
              checked={isInstant}
              onChange={handleInstantAdSwitchChange}
            />
          </SettingsOption>
        )}
        <TermsAndConditions
          checked={termsAccepted}
          onChange={handleChangeTerms}
        />
      </CardWrapper>
      {/* <Footer>
        <NewButton
          text={strings.dialog_ad_form.publish_step.submit}
          style={{ width: "100%", maxWidth: 336 }}
          size={NewButtonSize.LARGE}
          disabled={isFormInvalid}
          onClick={handleSubmit}
          loading={loading}
        />
      </Footer> */}
      <PriceFooter
        button={{
          text: strings.common.button.submit_and_continue,
          disabled: isFormInvalid,
          onClick: handleSubmit,
          loading: loading,
        }}
        isCampaign={props.isCampaign}
        viewCount={Number(viewCountHandler.rawValue)}
        clickCount={Number(clickCountHandler.rawValue)}
      />
      {isDateModalOpen && (
        <CapacityDatePicker
          isSponsored={false}
          onClose={() => setIsDateModalOpen(false)}
          helpText={
            strings.dialog_ad_form.publish_step.requires_approve +
            strings.dialog_ad_form.publish_step.only_for_view
          }
          previewMode={true}
          finalViewCount={calcViewCountForReservation(
            Number(viewCountHandler.rawValue),
            Number(clickCountHandler.rawValue),
            click_to_view_coef
          )}
        />
      )}
      {typeHelpModal && (
        <Modal
          title={strings.dialog_ad_form.publish_step.type_differences}
          primaryButton={{
            label: strings.common.button.understood,
            onClick: () => setTypeHelpModal(false),
            variant: NewButtonVariant.OUTLINED,
          }}
          onClose={() => setTypeHelpModal(false)}
        >
          <ul className={commonStyles.notes}>
            {strings.dialog_ad_form.publish_step.type_notes.map((note) => (
              <li>{note}</li>
            ))}
          </ul>
        </Modal>
      )}
    </>
  );
};

interface SettingsOptionProps {
  title: string;
  children: React.ReactNode;
  helpText?: string;
  onClick?: () => void;
}

const SettingsOption = ({
  title,
  helpText,
  children,
  onClick,
}: SettingsOptionProps) => (
  <div className={styles.settingsOption}>
    <div className={styles.optionContent} onClick={onClick}>
      <p>{title}</p>
      {children}
    </div>
    {helpText && (
      <div className={styles.helpContainer}>
        <Alert size={20} />
        <p>{helpText}</p>
      </div>
    )}
  </div>
);

export default PublishStep;
